// frontend/src/content/regression-en.js
// English content for Regression site. Mirrors regression-de.js structure exactly.
// Import via useContent() — never hardcode strings in components.

// ═══════════════════════════════════════════════════════════
// META — Page title & description (SEO)
// ═══════════════════════════════════════════════════════════
export const meta = {
  title: "Past Life Regression Sessions — Benjamin Kurtz",
  description: "Past Life Regression Sessions with Benjamin Kurtz. Hypnosis, soul work, and energy work — online or in person.",
};

// ═══════════════════════════════════════════════════════════
// HEADER — Legacy top nav (superseded by menu export below)
// ═══════════════════════════════════════════════════════════
export const header = {
  nav: [
    { label: "Regression", anchor: "#what-is" },
    { label: "For Whom?", anchor: "#for-whom" },
    { label: "Science", anchor: "#science" },
    { label: "About Benjamin", anchor: "#about" },
    { label: "Process", anchor: "#process" },
    { label: "Experiences", anchor: "#cases" },
  ],
  cta: "Free intro call",
};

// ═══════════════════════════════════════════════════════════
// HeroV3Section — HERO
// ═══════════════════════════════════════════════════════════
export const hero = {
  label: "BENJAMIN KURTZ ACADEMY",
  headlineLine1: "Your soul",
  headlineLine2: "remembers.",
  heroCta: "Are you ready to listen?",
  subline: "Past Life Regression · Hypnosis · Energy Work — online and in person.",
  ctaPrimary: "Book free intro call",
  ctaSecondary: "Listen to Podcast Episode →",
  videoUrl: "https://media.thelightcall.com/video/riverside_benjamin_raw-video-cfr_benjamin_kurtz's%20st_0016%20trim.mp4",
  posterUrl: "https://media.thelightcall.com/video-thumbnails/welcome_regresson_video_thumbnail.jpg",
  vimeoEmbedUrl: "https://player.vimeo.com/video/1168643769",
};

// ═══════════════════════════════════════════════════════════
// ServicesSection — SERVICES
// ═══════════════════════════════════════════════════════════
export const services = {
  items: [
    { label: "Remember Past Soul Lives", sub: "Past-Life Regression" },
    { label: "Reprogramming & Realignment", sub: "Hypnotherapy" },
    { label: "Master Reiki & Quantum Energy Work", sub: "Energy Work" },
  ],
  location: "online & on-site",
};

// ═══════════════════════════════════════════════════════════
// WelcomeSection — WELCOME
// ═══════════════════════════════════════════════════════════
export const welcome = {
  headline: "Hi, I'm Benjamin.\nGlad you're here.",
  quoteLines: [
    "Here you'll discover what regression and past life regression sessions are, how they can help you, and what really happens in a session.",
    "You'll also find podcast episodes, scientific background, and the opportunity to connect directly with me in a free intro call.",
    "I look forward to hearing from you.",
    "Enjoy exploring.",
  ],
  author: "Benjamin",
  imageUrl: "https://media.thelightcall.com/images/2%20s%20350px.png",
  signatureUrl: "https://media.thelightcall.com/images/signature_benjamin_black.png",
};

// ═══════════════════════════════════════════════════════════
// StatementSection — STATEMENT
// ═══════════════════════════════════════════════════════════
export const statement = {
  lines: [
    "There are experiences that our mind alone cannot grasp.",
    "Memories that don't just come from this life.",
    "Patterns that spring from beyond this existence.",
  ],
};

// ═══════════════════════════════════════════════════════════
// QuoteSection — Featured quote on primary blue
// ═══════════════════════════════════════════════════════════
export const quoteSection = {
  variant: 'dark',
  id: 'quote',
  // Brian Weiss always first. Add more quotes here — component rotates automatically.
  quotes: [
    {
      lines: [
        "The real you, the immortal you,",
        "is the you that is present",
        "from body to body, from life to life.",
      ],
      author: "Brian L. Weiss MD",
    },
    {
      lines: [
        "There are more things in heaven and earth",
        "than are dreamt of",
        "in our philosophy.",
      ],
      author: "Roger Woolger Ph.D.",
    },
    {
      lines: [
        "It seems to me that we have a sufficient",
        "number of cases strongly suggestive",
        "of rebirth.",
      ],
      author: "Ian Stevenson MD",
    },
    {
      lines: [
        "Even though it may seem hard to believe,",
        "evidence might exist that life after death",
        "is actually a reality.",
      ],
      author: "Jim B. Tucker MD",
    },
  ],
};

// ═══════════════════════════════════════════════════════════
// WhatIsSection — WHAT IS REGRESSION
// ═══════════════════════════════════════════════════════════
export const whatIs = {
  label: "REGRESSION SESSIONS",
  headline: "What does regression mean and what is a past life regression?",
  body: [
    "Regression — from the Latin regressus, meaning 'a step back' — means returning to earlier experiences, to older memories. To understand why you react the way you do today. Why certain patterns keep repeating. Why some themes feel as though you've carried them longer than this single lifetime.",
    "A past life regression goes further. It can answer why certain people feel strangely familiar even though you've just met them. Why particular gifts come naturally to you, or why certain topics consume you with a burning interest.",
    "Imagine a film strip: each individual frame is a life — from birth to death. Hold it close and you're inside it. Pull back and you see countless frames stretching in sequence. That is roughly how we experience and remember linear events. Some people, however, experience and remember soul lives in parallel — beyond what the rational mind can grasp.",
    "In a deep state of relaxation, where you remain in complete control at all times — we can open access to this stream of images and lives. What you encounter there is always exactly what you need in this moment.",
  ],
};

// ═══════════════════════════════════════════════════════════
// PodcastSection / PodcastVideoSection — PODCAST
// Note: podcasts[0] is used by both PodcastSection and PodcastVideoSection
// ═══════════════════════════════════════════════════════════
export const podcasts = [
  {
    id: "berggesundheit-052",
    label: "PODCAST, EPISODE #52",
    headline: "Reincarnation: What if death is not the end?",
    sectionHeadline: "Some remember beyond this life.",
    sectionBody: `"Suddenly things came together that were previously inexplicable." A regression session shows you not only what was — it shows you why you are the way you are. Benjamin in an extensive conversation with Tobias Berg about reincarnation, soul memory, and what really happens in a session.`,
    subline: "Benjamin in an extensive podcast conversation about past life regression, reincarnation, and what really happens in a session.",
    youtubeEmbedUrl: "https://www.youtube.com/watch?v=U5L07PmViis",
    youtubeTitle: "Reincarnation: What if death is not the end?",
    ctaBelow: "Book free intro call",
    listenLabel: "Listen to podcast conversation",
    episodeLabel: "Episode 52",
    episodeHost: "berggesundheit · Tobias Berg",
    episodeDuration: "1:14:32",
    episodeAudioUrl: "https://audio.podigee-cdn.net/2364431-m-438ee3d6d68b8de82a26986fecfd35e8.mp3",
    podcastLinks: [
      { label: "Spotify", url: "https://open.spotify.com/episode/3D9V2pK9kdT6ED9FENxpnW?si=7rD9pMrcSV6uLq-tOoMICA" },
      { label: "Apple", url: "https://podcasts.apple.com/pt/podcast/episode-52-reinkarnation-was-wenn-der-tod-nicht-das-ende-ist/id1680846180?i=1000751332298" },
      { label: "YouTube", url: "https://youtu.be/U5L07PmViis?si=r1aB93QVAkpFgd97" },
    ],
  },
];

// ═══════════════════════════════════════════════════════════
// ForWhomSection — FOR WHOM
// ═══════════════════════════════════════════════════════════
export const forWhom = {
  label: "FOR WHOM",
  headline: "Who comes to a regression session?",
  intro: "Some come with a specific issue: an anxiety that can't be explained. A relationship pattern that keeps repeating. Physical symptoms without a clear cause. A loss that won't heal. Others come with a bigger question: Why am I here? What should I do with this life? And some come out of pure curiosity — and find something they didn't expect.",
  columns: 4,
  topics: [
    { title: "Anxiety & Panic Attacks", body: "When the reaction is greater than the situation." },
    { title: "Relationship Patterns", body: "Why the same dynamics keep repeating." },
    { title: "Physical Symptoms", body: "When the body carries what the mind couldn't process." },
    { title: "Death Anxiety & Endless Life", body: "Understanding death as a transition, not an end." },
    { title: "Life Purpose & Meaning", body: "Why am I here — and what should I do with it?" },
    { title: "Contact with Deceased", body: "When someone has passed and something remained unsaid." },
    { title: "Spiritual Development", body: "Expanding consciousness beyond concepts." },
    { title: "Learning Channeling", body: "Opening the connection to other sources." },
  ],
};

// ═══════════════════════════════════════════════════════════
// AboutSection — ABOUT BENJAMIN
// ═══════════════════════════════════════════════════════════
export const about = {
  label: "ABOUT BENJAMIN KURTZ",
  headline: "What called me to this work",
  body: [
    "My first contact with past life regression was not a gentle awakening. I booked a day coaching because I had issues that even I as a coach couldn't solve alone. In one of the final hours — after almost ten hours of work — the coaches guided me into an earlier soul life. What I experienced there explained things I hadn't been able to understand for years. Through direct experience, it came alive and gave me clarity.",
    "I've been guiding people for over a decade — as NLP Master, Hypnotherapist, EMDR and Wingwave Practitioner, Reiki and Quantum Master Healer. With a background in entrepreneurship as a filmmaker and photographer, a Master's in Design, a spiritual practice that has led me from Cologne to India and into nomadic life, and the deep conviction: Regression sessions are not just another method. They are the space where something can reveal itself most deeply.",
  ],
  credentials: [
    "NLP Master & NR Practitioner",
    "Hypnosis Coach",
    "EMDR · wingwave® Coach",
    "Reiki Master Teacher (Usui)",
    "Systemic Constellation Work",
    "Breathwork & Meditation",
    "Past Life Regression",
    "Psilocybin Retreat Facilitator",
  ],
  credentialsLabel: "TRAININGS & CERTIFICATIONS",
  photoPlaceholder: "[ Photo Benjamin Kurtz ]",
};

// ═══════════════════════════════════════════════════════════
// ProcessSection — PROCESS
// ═══════════════════════════════════════════════════════════
export const processSection = {
  label: "THE PROCESS",
  headline: "From our first conversation to integration",
  steps: [
    {
      number: "01",
      title: "Free intro call",
      duration: "Free · 30 minutes",
      body: "A genuine first conversation. We look together at what you're seeking, I help you sort life topics, explain how a session works, and check if it's the right fit.",
    },
    {
      number: "02",
      title: "The Session",
      duration: "60–90 minutes · 1:1 online or in person",
      body: "You stay with yourself the entire time. I guide you into a deep relaxation state and navigate what appears there. You remain in control.",
    },
    {
      number: "03",
      title: "Integration",
      duration: "15–20 minutes · 5–10 days later",
      body: "What emerges in a session needs time to settle. This call gives you space to anchor the experience and clarify any open questions.",
    },
  ],
  footnote: "Many clients book multiple sessions — not because they have to, but because they want to. The first session opens so much more curiosity, as spaces often reveal themselves that were previously hidden.",
  cta: "Book free intro call",
};

// ═══════════════════════════════════════════════════════════
// CaseStudiesSection — CASE STUDIES
// ═══════════════════════════════════════════════════════════
export const cases = {
  label: "CASE STUDIES",
  headline: "Stories from practice — when souls remember",
  body: "When people share their stories, it shows their authentic strength. With admiration for their courage, I tell some of their cases and their positive effects here. All stories were shared with explicit consent. Names with * were changed at the client's request.",
  anonymityNote: "* Name has been changed",
  clickHint: "Click on one of the stories to read it.",
  items: [
    {
      name: "Marina", gender: "female",
      image: "https://media.thelightcall.com/client-portraits/Marina-%202.jpeg",
      title: "A New Blueprint",
      teaser: "Ankle-deep into the sea — impossible. An experience between lives resolved what years of therapy could not.",
      tag: "Death anxiety · Trauma · Between lives",
      situation: "Marina came with something she couldn't name: panic whenever the word death came up. Sweaty palms at the question \"What if you had only two weeks to live?\" A strong fear of losing control. And going ankle-deep into the sea — impossible, even though she loved the water. Her stepfather had died at sea from a blood clot. Seemingly the origin. But something was deeper.",
      session: "The trance was so deep that Marina could no longer speak — something that often happens and is entirely normal. We communicated through hand signals agreed on beforehand. She found herself in darkness and followed a light. What appeared was not trauma: she was no longer a solid form, but a permeable sphere — without a body. On a large stage with other beings like her. Marina received the blueprint to become human — like DNA entering a cell. She was between lives. She perhaps experienced the origin of her own incarnation.",
      result: "When I asked her the question again afterward — two weeks to live — she was completely calm: \"Then I would tell people I love that I love them.\" That same evening she walked hip-deep into the sea. No panic. A year later: a changed perception of life, more openness, more curiosity. Even her belly felt different — no longer heavy as before, but warm, pleasantly dark, like a sky without stars. A deep primal trust that nothing truly ends — only the form, not life.",
    },
    {
      name: "Anna", gender: "female",
      image: "https://media.thelightcall.com/client-portraits/Anna-krickel.jpg",
      title: "Back Home",
      teaser: "She felt like a different person in conflict. The answer lay thirty years back.",
      tag: "Trauma · Channeling · Family System",
      situation: "She came with explosive reactions in interpersonal conflicts — and the feeling of not being herself in those moments. Something that often overshadowed her, yet didn't feel at all like her own shadow. In the intake conversation, a possible connection emerged: a grandmother who had taken her own life before Anna was born.",
      session: "In trance Anna found herself in a space of shadows — not on earth, not at home. She was someone else: the grandmother, trapped between worlds, full of shame, convinced she had made a mistake, with no way back into the light. From this perspective she saw little Anna below on earth, three years old. Then the shift — with the help of a being from the in-between, Anna became herself. From there she entered a pre-birth space, where another being showed her a ritual: hands like a bowl, inviting light — to remember the origin, the source, even after birth. She passed this ritual on to the grandmother. Two minutes of silence. Eyes flickering. Then, very quietly: \"I just guided her home.\"",
      result: "Perhaps the shadow had been the grandmother all along. The explosive reactions, the strained nervous system in conflicts — perhaps attempts at contact from the in-between worlds. In the session a picture also emerged of why the grandmother had taken her own life back then — something that brought peace to the entire family. Today Anna feels a clear connection to her angels. The connection to the source of light — once established — stays.",
    },
    {
      name: "Javi", gender: "male",
      image: "https://media.thelightcall.com/client-portraits/javi.jpg",
      title: "The Fear of Not Being Enough",
      teaser: "1730. A village. A house by the lake. And the explanation for an anxiety no mind could solve.",
      tag: "Relationship Anxiety · Soul Life · Pattern Resolution",
      situation: "Javi was waiting for a woman he had gotten to know over months — their first shared weekend was approaching. He had prepared everything perfectly. And yet: an inner tension, an over-tuning, an anxiety beneath it he knew without knowing it. The fear of not being enough.",
      session: "It moved very fast. Javi was in 1730, between 28 and 32 years old. He worked with a business partner. That partner's wife had fallen in love with him — he hadn't encouraged her, nothing had happened. But the business partner became jealous, made up stories about him, and had him driven out of the village. Then darker scenes: a cauldron with black liquid, enslavement, prison — punishment for something he hadn't done. Four or five years later, a different picture: a house by the lake, a happy woman, children playing. A house he called home, where he found absolute inner peace.",
      result: "The fear of today had its origin in 1730 — the experience of doing everything right and still losing everything. But also the lost memory that after loss, a happy home can exist. An arrival, a being-at-home — deeper than the habitual mind can grasp. When Javi understood this, the inner pressure transformed. He and his partner are living together happily near Valencia today, with a view of the water.",
    },
  ],
  sectionLabels: {
    situation: "Starting Situation",
    session: "In the Session",
    result: "Result",
  },
};

// ═══════════════════════════════════════════════════════════
// ResearchersSection / EvidenceSection — RESEARCHERS & EVIDENCE
// portraitFocus options: "center" | "top" | "bottom" | "left" | "right"
//   or combined: "top-left" | "top-right" | "bottom-left" | "bottom-right"
// featured: true → author appears in the Featured Researcher hero block
// showInQuotes: false → excludes author from ResearcherQuotesSection
// ═══════════════════════════════════════════════════════════
export const researchers = {
  authorBigLabel: "REGRESSION LEGENDS",
  authorHeadline: "When researchers speak from experience",
  authorLabel: "WHAT RESEARCH SAYS",
  accordion: {
    readMore: "Read more",
    readLess: "Read less",
  },
  authors: [
    {
      id: "roger-woolger",
      name: "Roger J. Woolger Ph.D.",
      role: "Jungian Analyst, Regression Therapist, Teacher",
      lifeDates: "*1944–2011",
      portrait: "https://media.thelightcall.com/authors/roger_woolger.jpg",
      portraitFocus: "center",
      portraitFocal: { x: 50, y: 50 },
      quote: "There are more things in heaven and earth than are dreamt of in our philosophy.",
      featuredQuote: "There are more things in heaven and earth than are dreamt of in our philosophy.",
      shortVersion: "Jungian analyst, regression therapist and teacher. Author of 'Other Lives, Other Selves' (1987) — translated into 8 languages. Co-founder of EARTh and founder of Deep Memory Process (DMP).",
      longVersion: `Roger Woolger was a Jungian analyst and regression therapist with a distinguished academic background: degrees in Psychology, Philosophy, and Comparative Religion from Oxford University and King's College London, training at the C.G. Jung Institute in Zurich, and service as Guest Professor at Vassar College. He was a co-founder of EARTh (European Association for Regression Therapy) and helped establish professional standards for the field. His special gift was making inner processes not just psychologically but spiritually readable: from dream and symbol work to Sufism, Western mysticism, and poetry, woven with music, story, meditation, and humor.

"Other Lives, Other Selves" (1987) is one of the most influential works in regression therapy — translated into 8 languages. The book presents a synthesis of Jungian depth psychology, bodywork, psychodrama, yoga psychology, shamanic perspectives, and meditative principles, including transitional and intermediate states from Tibetan Buddhist teachings. Woolger developed the concept of "past-life complexes" as an extension of Jung's complex theory — the idea that "the psychic, emotional, and physical impressions laid down in one lifetime are in some way transmitted to future lives." This framework provided a theoretical bridge between analytical psychology and regression therapy.

From his decades-long practice, Woolger developed Deep Memory Process (DMP): a creative synthesis combining Jungian active imagination, Reichian somatic body therapy, Moreno's psychodrama, Gestalt therapy, and Tibetan Buddhist bardo wisdom. DMP is an approach for deep memory access that combines regression, embodiment, dreamwork, meditation, and regression shamanism. He published multiple articles in the Journal of Regression Therapy including "Imaginal Techniques in Past-Life Therapy" (Vol. I, No. 1, 1986) and "Aspects of Past-Life Bodywork" (Issues 3 and 4). He trained therapists internationally and shaped Spiritual Healing Conferences where themes like regression & shamanism, soul loss, and the sacred connection of body and spirit took center stage. His position was clear: "So many cases of 'incurable' depression, phobias, anxieties, and physical illnesses find complete resolution when the cause is healed from beyond the current life."`,
      sourceLabel: "Source",
      sourceUrl: "https://regressionjournal.org/jrt_author/roger-woolger-1944-2011/",
    },
    {
      id: "ian-stevenson",
      name: "Ian Stevenson M.D.",
      role: "Founder of DOPS, University of Virginia (1967)",
      lifeDates: "*1918–2007",
      portrait: "https://media.thelightcall.com/authors/Ian_Stevenson_3_beige.jpg",
      portraitFocus: "center",
      portraitFocal: { x: 50, y: 20 },
      quote: "It seems to me that we have a sufficient number of cases, ... something strongly suggestive of rebirth.",
      featuredQuote: "It seems to me that we have a sufficient number of cases, ... something strongly suggestive of rebirth.",
      shortVersion: "Over 2,500 cases studied worldwide. Founder of scientific reincarnation research and the Division of Perceptual Studies (1967) at the University of Virginia.",
      longVersion: `Ian Stevenson is considered the founder of scientific reincarnation research. In 1961, he began systematic investigations of children who reported spontaneous memories of past lives. His first trip to India in 1961 was groundbreaking: in four weeks, he found 25 cases, and he realized these phenomena were much more common than assumed.

Over four decades, Stevenson investigated over 2,500 cases worldwide. His work is characterized by rigorous scientific methodology: each case was documented on site, memories compared with historical facts, and alternative explanations systematically excluded. In 1966, he published his landmark book "Twenty Cases Suggestive of Reincarnation," reviewed favorably in the American Journal of Psychiatry with the note: "In regard to reincarnation he has painstakingly and unemotionally collected a detailed series of cases in India in which the evidence is difficult to explain on any other grounds." This was followed by the four-volume series "Cases of the Reincarnation Type" (1975-1983) with cases from India, Sri Lanka, Lebanon, Turkey, Thailand, and Burma — and his magnum opus "Reincarnation and Biology" (1997), a 2,200-page work documenting over 200 cases where children's birthmarks corresponded to wounds on deceased persons.

The results are remarkable: children could provide detailed information about families, locations, and circumstances of people who had died years before their birth. Many of these details were later verified. Stevenson discovered that over 70% of cases involved unnatural deaths, and many children exhibited behaviors and phobias that appeared connected to the previous life. In cases involving violent death, over 35% of children showed phobias related to the mode of death. He proposed reincarnation as a "third contributing factor" alongside genetics and environment. As he famously stated: "I think a rational person, if he wants, can believe in reincarnation on the basis of evidence." His successor Jim Tucker continues this work at the University of Virginia's Division of Perceptual Studies, where the research continues to this day.`,
      sourceLabel: "Source",
      sourceUrl: "https://med.virginia.edu/perceptual-studies/wp-content/uploads/sites/360/2015/11/REI35.pdf",
    },
    {
      id: "jim-tucker",
      name: "Jim B. Tucker M.D.",
      role: "Bonner-Lowry Professor of Psychiatry and Neurobehavioral Sciences (Emeritus)",
      lifeDates: "–present",
      portrait: "https://media.thelightcall.com/authors/Jim_Tucker.jpg",
      portraitFocus: "center",
      portraitFocal: { x: 100, y: 20 },
      quote: "Even though it may seem hard to believe, evidence might exist that life after death is actually a reality.",
      featuredQuote: "Even though it may seem hard to believe, evidence might exist that life after death is actually a reality.",
      shortVersion: "Groundbreaking quantitative study of American children with past-life memories. The case of James Leininger — a two-year-old's memories of a WWII pilot with historically verified details — is considered one of the best documented worldwide.",
      longVersion: `Jim B. Tucker, M.D. is Stevenson's direct successor at the University of Virginia's Division of Perceptual Studies (DOPS). His groundbreaking work focuses on American children with reincarnation memories, proving that these phenomena occur worldwide, independent of cultural beliefs or parental expectations. His study of 799 cases was the first quantitative scientific analysis in the United States and established the foundation for systematic research of this phenomenon.

Tucker developed the Strength of Case Scale (SOCS), a revolutionary quantitative instrument measuring evidential strength across four scientific criteria: birthmarks, statement accuracy, relevant behaviors, and emotional connection. Thanks to this standardized method, the DOPS database has identified consistent patterns: approximately 70% of deceased persons died from unnatural causes, the average interval between death and rebirth is about 16 months, and roughly 30% of cases involve birthmarks or birth defects corresponding to wounds from the previous life.

One of his most famous cases is James Leininger: a two-year-old from Louisiana who described detailed memories of a WWII pilot's life. The child could name specific details: the pilot's name (James Huston), squadron (VF-38), plane type (F4U Corsair), precise circumstances of death (shot down near Iwo Jima), and even names of comrades. His parents later verified all details as historically accurate. Tucker's position reflects scientific diligence: "If it's their imagination, then in the detailed cases, that's one heck of a coincidence." His work demonstrates that these cases deserve serious scientific attention — though he himself exclusively studies spontaneous memories in children, not hypnotic regression therapy.`,
      sourceLabel: "Source",
      sourceUrl: "https://med.virginia.edu/perceptual-studies/our-research/children-who-report-memories-of-previous-lives/",
    },
    {
      id: "brian-weiss",
      featured: true,
      showInQuotes: false,
      name: "Brian L. Weiss M.D.",
      role: "Chairman Emeritus of Psychiatry, Mount Sinai Medical Center",
      lifeDates: "*1944–present",
      portrait: "https://media.thelightcall.com/authors/brian-weiss_350.jpg",
      portraitAccordion: "https://media.thelightcall.com/authors//brian_2.jpg",
      portraitFocus: "top",
      portraitFocal: { x: 70, y: 20 },
      portraitFocalAccordion: { x: 50, y: 80 },
      quote: "When you feel better as a result of a past life recall experience... you don't need to question the logical validity of the experience.",
      featuredQuote: "The real you, the immortal you, is the you that is present from body to body, from life to life.",
      shortVersion: "'Many Lives, Many Masters' (1988) — international bestseller with over 1 million copies sold. A skeptic's attitude transformed through a patient who, under hypnosis, described past lives.",
      longVersion: `Brian Weiss was a highly respected psychiatrist and Chief of Staff at Mount Sinai Medical Center in Miami when a case in 1980 would fundamentally change his work. Catherine, a patient with severe anxiety and phobias, spoke under hypnosis of detailed memories from past lives — information that could be historically verified and that led to rapid symptom resolution. Weiss, a graduate of Columbia University (B.A.) and Yale University School of Medicine (M.D.), had been trained in conventional psychiatry. His psychiatric education had taught him that everything could be explained in the current life.

What began as a single therapeutic case led to decades of clinical work with thousands of patients. Weiss systematically documented past-life regression sessions and their long-term healing effects. His book "Many Lives, Many Masters" (1988) became an international bestseller with over 1 million copies sold and made regression therapy accessible to a broad audience. This was followed by "Through Time Into Healing" (1992) with clinical case studies and self-regression techniques, "Same Soul, Many Bodies" (2004) on future-life progression, and "Miracles Happen" (2012).

Weiss emphasizes that he was initially skeptical — he has stated: "When I say 'agnostic,' it was like, I don't know that I believe in God, even." Yet the consistent results with different patients could not be ignored. His key insights include: "We are spiritual beings, not human beings. Understanding dissolves fear," and "Many phobias and ailments are rooted in past-life experiences whose acknowledgement by the patient can have a curative effect." His work has opened the path to regression-based methods for thousands of therapists worldwide. Important note: Weiss has not published peer-reviewed scientific articles; his evidence is presented through popular books, workshops, and media appearances.`,
      sourceLabel: "Source",
      sourceUrl: "https://www.brianweiss.com/about-the-books/many-lives-many-masters/",
    },
  ],
};

// ═══════════════════════════════════════════════════════════
// ReferencesSection — FURTHER LITERATURE
// type: "journal" | "book" | "audiobook" (extensible)
// ═══════════════════════════════════════════════════════════
export const references = {
  bigLabel: "FURTHER REFERENCES",
  showMore: "More References",
  showLess: "Less",
  headline: "Deepening & Sources",
  scrollCtaText: "Ready to have your own experience?",
  scrollCtaAriaLabel: "Continue to booking",
  items: [
    {
      type: "journal",
      name: "International Journal of Regression Therapy",
      dates: "1986–present",
      url: "https://regressionjournal.org",
      role: "regressionjournal.org",
      portrait: "https://media.thelightcall.com/images/paste-1772670904056.png",
      description: `The International Journal of Regression Therapy is the annually published peer-reviewed journal, published since 1986. Throughout these years the journal has published articles on the empirical effects and theoretical implications of regression therapy.

Since 2021, the editorial board decided to grant access to all articles published in the journal's 35+ year history. In this new era, under the auspices of the European Association for Regression Therapy (EARTh), researchers and therapists from all approaches have access to this wealth of information shared by experienced pioneers of the field.`,
      sourceLabel: "Source",
      sourceUrl: "https://regressionjournal.org",
    },
    {
      type: "book",
      name: "Many Lives, Many Masters",
      dates: "1988",
      authors: "Brian L. Weiss",
      url: "https://brianweiss.com/books/many-lives-many-masters",
      role: "BRIAN L. WEISS M.D.",
      portrait: "https://media.thelightcall.com/authors//manylivesmanymasters.jpeg",
      description: `The international bestseller that opened regression therapy to the world. Brian Weiss documents his transformative work with Catherine, a patient who under hypnosis began reporting details from past lives — information that could be historically verified.

What began as a therapeutic intervention led to decades of clinical work with thousands of patients. Many Lives, Many Masters became one of the most influential books in spiritual psychology and brought regression therapy to a broad audience.`,
      sourceLabel: "To the book",
      sourceUrl: "https://brianweiss.com/books/many-lives-many-masters",
    },
    {
      type: "book",
      name: "Other Lives, Other Selves",
      dates: "1987",
      authors: "Roger J. Woolger Ph.D.",
      url: "https://www.amazon.com/Other-Lives-Selves-Psychotherapist-Discovers/dp/0553345958",
      role: "Roger J. Woolger Ph.D.",
      portrait: "https://media.thelightcall.com/authors//otherlivesotherselfes.jpg",
      description: `One of the most influential works in regression therapy. Roger Woolger presents a synthesis of Jungian depth psychology, bodywork, psychodrama, yoga psychology, and shamanic perspectives.

The book shows how regression therapy can serve as a tool for resolving current life problems through the understanding of past lives. Woolger integrates Western psychology with Eastern spiritual traditions, creating a comprehensive approach to soul work.`,
      sourceLabel: "To the book",
      sourceUrl: "https://www.amazon.com/Other-Lives-Selves-Psychotherapist-Discovers/dp/0553345958",
    },
    {
      type: "book",
      name: "Life Before Life",
      dates: "2005",
      authors: "Jim B. Tucker M.D.",
      url: "https://www.amazon.com/Life-Before-Childrens-Memories-Previous/dp/031237674X",
      role: "Jim B. Tucker M.D.",
      portrait: "https://media.thelightcall.com/authors//lifebeforelife.jpg",
      description: `Jim Tucker presents a thorough investigation of research into children's reports of memories of previous lives — a collection of 2,500 cases at the University of Virginia studied carefully since Ian Stevenson began this work over forty years ago.

Children typically begin speaking of a previous life at age two or three, describing a former family or a previous death. Their statements have often proven accurate for specific deceased individuals, and some children have recognized members of the former family. A number also had birthmarks or defects matching wounds on the deceased person's body.`,
      sourceLabel: "To the book",
      sourceUrl: "https://www.amazon.com/Life-Before-Childrens-Memories-Previous/dp/031237674X",
    },
  ],
};

// ═══════════════════════════════════════════════════════════
// SimpleCTA — Minimal CTA block (text only)
// ═══════════════════════════════════════════════════════════
export const simpleCta = {
  label: "YOUR NEXT STEP",
  headline: "Ready to listen to your soul?",
  subline: "A non-binding initial conversation. We look together at whether a session is the right step for you.",
  cta: "Book free intro call",
};

// ═══════════════════════════════════════════════════════════
// CtaImage — CTA block with portrait image
// ═══════════════════════════════════════════════════════════
export const ctaImage = {
  label: "YOUR NEXT STEP",
  headline: "Ready to listen to your soul?",
  subline: "A non-binding initial conversation. We look together at whether a session is the right step for you. No pressure, no expectations — just a clarifying conversation.",
  cta: "Book free intro call",
  imageUrl: "https://media.thelightcall.com/images/2%20s%20350px.png",
  imageAlt: "Benjamin Kurtz",
};

// ═══════════════════════════════════════════════════════════
// TestimonialsSection / TestimonialCarousel — WALL OF LOVE
// ═══════════════════════════════════════════════════════════
export const testimonials = {
  clientLabel: "WALL OF LOVE",
  clientSubtitle: "Experiences that remain",
  clients: [
    {
      quote: "Through Benjamin's calm and clarity I was able to engage with the session quickly despite initial nervousness. It's as if he sees through you and immediately guides you to places you've never known — but that are exactly what you need. It was indescribable to be in a place of pure love beyond matter, to meet my angels, and to rest in this place of absolute peace — one I can now return to in everyday life. The integration afterward was very valuable for truly anchoring the insights within me.",
      name: "Anna K.",
      context: "Health Coach, Germany",
      image: "https://media.thelightcall.com/client-portraits/Anna-krickel.jpg",
    },
    {
      quote: "Everything Benjamin did with us happened with ease. I came into contact with myself and the others very quickly. Benjamin created a clear space for openness, learning, and joy.",
      name: "Alexander W.",
      context: "Music Producer, USA",
      image: "https://media.thelightcall.com/client-portraits/alex%20w%202.jpg",
    },
    {
      quote: "I laughed, I cried, I was back in my childhood, I was everywhere. Through Benjamin's coaching I was able to dive deep into my emotions.",
      name: "Arthur K.",
      context: "Photographer, Germany",
      image: "https://media.thelightcall.com/client-portraits/arthur.jpg",
    },
    {
      quote: "I wanted to tell you that I feel very happy — thanks to the teachings and tools I received during the retreat. They help me enormously to stay centered and let everything flow harmoniously.",
      name: "Hernan D.",
      context: "Lifeguard, Argentina",
      image: "https://media.thelightcall.com/client-portraits/hernan.jpg",
    },
  ],
};

// ═══════════════════════════════════════════════════════════
// BookingSection — BOOKING FORM
// ═══════════════════════════════════════════════════════════
export const booking = {
  label: "READY FOR A CONVERSATION?",
  headline: "Book a free intro call.",
  subline: "Free and non-binding. 30 minutes. We look together at what moves you — and whether a session is the next sensible step.",
  formPlaceholder: "[ Fluent Forms Embed ]",
  directBookingText: "You want to choose a time slot directly?",
  directBookingCta: "Book free intro call",
  directBookingUrl: "https://www.benjaminkurtz.de/bookings/wakeup",
  calendarUrl: "https://www.benjaminkurtz.de/booking/benkurtz_duw17n0e/wakeup?embedded=1",
  formTopics: [
    "Past Life Regression",
    "Trauma / emotional blocks",
    "Understanding recurring relationship patterns",
    "Learning channeling",
    "Life purpose / Meaning",
    "Spiritual awakening",
  ],
};

// ═══════════════════════════════════════════════════════════
// FAQSection — FREQUENTLY ASKED QUESTIONS
// ═══════════════════════════════════════════════════════════
export const faq = {
  bigLabel: "QUESTIONS & ANSWERS",
  headline: "Frequently asked questions about regression",
  items: [
    {
      question: "What's the difference between a regression session and past life regression?",
      answer: `The terms are often used interchangeably. Regression is the umbrella term: going back to earlier experiences. Past life regression — or soul regression, reincarnation therapy — goes specifically into memories of previous lives.
In practice: a session can also focus on early childhood or the current lifetime — but for most clients, the path naturally leads into soul lives, because that is often where the roots of current patterns lie.
Important: you don't need to believe in anything. The experience speaks for itself.`,
    },
    {
      question: "Do I have to believe in reincarnation?",
      answer: `No. In fact, many clients come without fixed spiritual beliefs — often out of skepticism or pure curiosity.

Research from the University of Virginia (Ian Stevenson, Jim Tucker) shows that children worldwide spontaneously report memories of previous lives, independent of cultural background or parental beliefs. The experiences in sessions speak for themselves — belief is not a prerequisite.

What counts: your willingness to be open — to whatever shows up.`,
    },
    {
      question: "Is this channeling?",
      answer: `No. Regression and channeling are different practices.

Regression: you always remain yourself. All experiences are your own — as a child, as an adult, or in another soul life. You have control, you decide what you share.

Channeling: a different entity or energy source moves to the foreground. The "I" steps back; information comes from "outside."

In regression sessions, people sometimes experience encounters — but the focus always remains on your experience, your path, your healing.`,
    },
    {
      question: "What actually happens in a session?",
      answer: `You are always awake, conscious, and in full control. I guide you into a deep relaxation state — similar to meditation, only guided.

Within that state, you can gain access to images, emotions, and body sensations — sometimes like a film, sometimes like remembering, sometimes like feeling. Whatever comes is right.

Most clients describe it as: "I don't know if it's real — but it feels true." And that is enough. Changes show up in everyday life afterward.`,
    },
    {
      question: "Is there scientific evidence?",
      answer: `The research is just beginning — but there is more than most people realize:

• **University of Virginia (DOPS)**: Approximately 3,000 documented children's cases with verifiable memories of previous lives. Around 300 peer-reviewed publications.
• **Journal of Regression Therapy**: Since 1986, all articles freely accessible, documenting therapeutic outcomes.
• **Neuroimaging studies**: Show that states like meditation and regression are not "pathological" states — but non-ordinary, fully functional states of consciousness.

Honestly: no randomized controlled trials by mainstream standards. But thousands of documented cases — and the most important study is always you, your own experience.`,
    },
    {
      question: "Who is this suitable for?",
      answer: `People come with very different topics:

• **Anxiety & panic attacks** — when the reaction is greater than the situation
• **Relationship patterns** — why the same dynamics keep repeating
• **Physical symptoms** — without a clear medical cause
• **Death anxiety** — understanding death as a transition
• **Life purpose** — why am I here?
• **Curiosity** — simply experiencing what's there

Exclusions: acute psychosis, severe depression without therapeutic support. When in doubt: we clarify this together in the free intro call.`,
    },
    {
      question: "Can I do a session in German?",
      answer: `Yes. German is my mother tongue — sessions take place in German unless you expressly request English.

Many clients find it easier to reach deeper states in their native language. Images, words, and feelings come more naturally that way.`,
    },
    {
      question: "How long does the effect last?",
      answer: `That varies individually. Some clients report immediate, noticeable change — patterns dissolve, symptoms become lighter. Others describe it like a seed that grows over weeks and months.

The integration call after the session is important. Many experience new insights even weeks later, or begin to understand connections that hadn't been clear before.

A session is not a one-time "fix" — but often a decisive turning point.`,
    },
    {
      question: "What if I don't see anything?",
      answer: `That's a common concern — but it rarely happens. Every person has access to inner images, sensations, and perceptions. Sometimes it comes visually (like a film), sometimes as a feeling, sometimes as a knowing, sometimes as a body sensation.

Whatever comes is right. There is no "correct" or "wrong" in a session. Sometimes what does NOT come is also information — or it arrives in a second moment.

My job is to guide you to where you have access — and that is different for everyone.`,
    },
    {
      question: "Can I just try it out to see if it's for me?",
      answer: `Yes. The free intro call is exactly for that — free, non-binding, 30 minutes. We look together at what moves you, whether a session fits, and you can sense whether the connection feels right.

Many book their first session afterward. Some decide to wait a little longer. Both are completely fine. It's your journey, your timing.`,
    },
  ],
};

// ═══════════════════════════════════════════════════════════
// Footer — FOOTER
// ═══════════════════════════════════════════════════════════
export const footer = {
  brand: "Benjamin Kurtz Academy LLC",
  disclaimerLinkKeyword: "free intro call",
  tagline: "Grounded Spirituality\nAligned with Your Purpose",
  contactLabel: "Contact us:",
  legalLabel: "Legal",
  followLabel: "Follow",
  disclaimerTitle: "Legal Disclaimer",
  disclaimer: "Regression sessions and hypnosis are not a substitute for medical or psychiatric treatment. For mental health conditions, please consult a licensed physician or psychotherapist. If in doubt, you're still welcome to book a free intro call — I can advise you competently on this.",
  introCallLabel: "Initial consultation",
  copyright: {
    prefix: "All rights reserved. Home:",
    homeLabel: "benjaminkurtz.de",
    homeUrl: "https://benjaminkurtz.de",
  },
  contact: [
    { label: "WhatsApp", url: "https://wa.me/4915121231244", icon: "whatsapp" },
    { label: "E-Mail", url: "mailto:hello@benjaminkurtz.de", icon: "mail" },
    { label: "Instagram", url: "https://instagram.com/benjaminkurtz_de", icon: "instagram" },
  ],
  legalLinks: [
    { label: "Terms", url: "https://www.benjaminkurtz.de/terms/" },
    { label: "Privacy", url: "https://www.benjaminkurtz.de/privacy/" },
    { label: "Imprint", url: "https://www.benjaminkurtz.de/imprint/" },
  ],
  social: [
    { label: "Spotify", url: "https://open.spotify.com/episode/3D9V2pK9kdT6ED9FENxpnW?si=7rD9pMrcSV6uLq-tOoMICA" },
    { label: "Apple", url: "https://podcasts.apple.com/pt/podcast/episode-52-reinkarnation-was-wenn-der-tod-nicht-das-ende-ist/id1680846180?i=1000751332298" },
    { label: "YouTube", url: "https://youtu.be/U5L07PmViis?si=r1aB93QVAkpFgd97" },
  ],
};

// ═══════════════════════════════════════════════════════════
// UI Strings — Shared button/label text used across components
// ═══════════════════════════════════════════════════════════
export const ui = {
  showMore: "More",
  showLess: "Less",
  menuOpen: "Close menu",
  menuClosed: "Open menu",
  rewind30: "30 seconds back",
  forward30: "30 seconds forward",
  pause: "Pause",
  play: "Play",
  clickForMoreInfo: "Click for more information",
};

// ═══════════════════════════════════════════════════════════
// TranscriptPage — Token-gated transcript viewer (/transkript)
// ═══════════════════════════════════════════════════════════
export const transcriptPage = {
  accessDenied: {
    title: "Access denied.",
    message: "This link is invalid or has expired.",
  },
  toolbar: {
    title: "Transcript",
    themeToggleDark: "Dark",
    themeToggleLight: "Light",
    fontSizeDecreaseLabel: "Decrease font size",
    fontSizeIncreaseLabel: "Increase font size",
    themeToggleLabel: "Toggle background",
  },
  footer: {
    copyright: "© Benjamin Kurtz Academy · For registered recipients only",
  },
};

// ═══════════════════════════════════════════════════════════
// NotFoundPage — 404 page
// ═══════════════════════════════════════════════════════════
export const notFound = {
  brandName: "Benjamin Kurtz",
  title: "Page not found",
  message: "The page you're looking for doesn't exist or has been moved. Let's go back home.",
  homeButtonLabel: "To home page",
  homeAriaLabel: "To home page",
  homeButtonAriaLabel: "To home page",
};

// ═══════════════════════════════════════════════════════════
// PodcastVideoSection — Podcast video panel with transcript download
// ═══════════════════════════════════════════════════════════
export const podcastVideo = {
  panelLabel: "Transcript",
  panelTitleLine1: "Download now",
  panelTitleLine2: "for free",
  closeButtonLabel: "Close",
  emailPrompt: "Enter your email address and receive the complete transcript of the podcast episode directly in your inbox.",
  nameLabel: "Name",
  namePlaceholder: "Your first name",
  emailLabel: "Email address",
  emailPlaceholder: "your@email.com",
  submitButtonLabel: "Request transcript",
  downloadButtonLabel: "Download transcript",
  privacyNoteLine1: "No spam. You'll only receive the link to the transcript.",
  privacyNoteLine2: "You can unsubscribe at any time.",
  fluentFormsNote: "[ Fluent Forms Embed will be used here ]",
};

// ═══════════════════════════════════════════════════════════
// Menu — Sidecar navigation (replaces legacy header.nav)
// mainNav: top-level items shown in the sidecar header bar
// items: full nested structure for sidecar panel
// ═══════════════════════════════════════════════════════════
export const menu = {
  header: {
    logo: {
      src: "https://media.thelightcall.com/logos/BKA%20logo%20500%20px%20black.png",
      alt: "Benjamin Kurtz Academy"
    },
    cta: {
      label: "Free intro call",
      anchor: "#booking"
    },
    variant: "sidecar",
    mainNav: [
      { label: "Regression", anchor: null, hasSubmenu: true },
      { label: "Science", anchor: null, hasSubmenu: true },
      { label: "Experiences", anchor: null, hasSubmenu: true },
      { label: "About", anchor: null, hasSubmenu: true },
      { label: "FAQ", anchor: null, hasSubmenu: true },
    ],
  },
  overviewLabel: "Overview",
  items: [
    {
      id: "regression",
      label: "Regression",
      order: 10,
      children: [
        { id: "regression-welcome", label: "Welcome", anchor: "#hero", order: 11 },
        { id: "regression-what", label: "What is Regression?", anchor: "#what-is", order: 12 },
        { id: "regression-for-whom", label: "Who comes for regression?", anchor: "#for-whom", order: 13 },
        { id: "regression-process", label: "Session Process", anchor: "#process", order: 14 },
      ],
    },
    {
      id: "science",
      label: "Science",
      order: 20,
      children: [
        { id: "science-evidence", label: "Evidence", anchor: "#science", order: 21 },
        { id: "science-authors", label: "Researchers & Authors", anchor: "#science", order: 22 },
        { id: "science-references", label: "References", anchor: "#references", order: 23 },
      ],
    },
    {
      id: "experiences",
      label: "Experiences",
      order: 30,
      children: [
        { id: "experiences-cases", label: "Case Studies", anchor: "#cases", order: 31 },
        { id: "experiences-love", label: "Wall of Love", anchor: "#wall-of-love", order: 32 },
        { id: "experiences-youtube", label: "Podcast (YouTube)", anchor: "#podcast-video", order: 33 },
        { id: "experiences-audio", label: "Audio Podcast", anchor: "#podcast", order: 34 },
      ],
    },
    {
      id: "about",
      label: "About Benjamin Kurtz",
      anchor: "#about",
      order: 40,
    },
    {
      id: "faq",
      label: "FAQ",
      order: 50,
      children: [
        { id: "faq-0", label: "Regression vs. Past Life Regression?", anchor: "#faq-0", order: 51 },
        { id: "faq-1", label: "Must I believe in reincarnation?", anchor: "#faq-1", order: 52 },
        { id: "faq-2", label: "Is this channeling?", anchor: "#faq-2", order: 53 },
        { id: "faq-3", label: "What happens in a session?", anchor: "#faq-3", order: 54 },
        { id: "faq-more", label: "More Answers", anchor: "#faq", order: 55 },
      ],
    },
  ],
  footer: {
    items: [
      { label: "Regression", anchor: "#what-is" },
      { label: "Science", anchor: "#science" },
      { label: "For Whom?", anchor: "#for-whom" },
      { label: "About Benjamin", anchor: "#about" },
      { label: "Process", anchor: "#process" },
      { label: "Experiences", anchor: "#cases" },
      { label: "FAQ", anchor: "#faq" },
      { label: "References", anchor: "#references" },
    ],
  },
};

// Default export for backwards compatibility
export default {
  meta, header, hero, services, welcome, statement, quoteSection, whatIs, podcasts,
  forWhom, about, processSection, cases, researchers, references,
  simpleCta, ctaImage, testimonials, booking, faq, footer, ui,
  transcriptPage, notFound, podcastVideo, menu,
}
